import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
} from "@mui/material";
import { Account } from "Components/Shared/Account/Account";
import { ManualBankAccountVerificationPrompt } from "Components/Users/ConnectedBankAccounts/ManualBankAccountVerificationPrompt";
import { VerificationStatusChip } from "Components/Shared/Account/VerificationStatusChip";
import { PaperRow } from "Components/Shared/PrimaryPaper";
import {
  type BankAccountListItem,
  BankAccountStatus,
  type BankVerificationSource,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { format } from "date-fns";

type Props = {
  bankAccount: BankAccountListItem;
  userID: number;
  verifiedIBAN?: string | null;
  bankVerificationSource?: BankVerificationSource | null;
  isReadOnly?: boolean;
};

export const ConnectedBankAccount = ({
  bankAccount,
  userID,
  verifiedIBAN,
  bankVerificationSource,
  isReadOnly,
}: Props) => {
  const { t } = useResource();

  const { iban, bankAccountID, ownersNames, dateCreated, status, publicID } =
    bankAccount;
  const isUserVerified = !!verifiedIBAN && !!bankVerificationSource;
  const isBankIdentityVerification = isUserVerified && iban === verifiedIBAN;

  return (
    <Accordion
      key={bankAccountID}
      disableGutters
      elevation={0}
      sx={({ palette }) => ({
        border: `1px solid ${palette.divider}`,
        borderBottom: "0",
        "&:last-child": {
          borderBottom: `1px solid ${palette.divider}`,
        },
        "&::before": {
          display: "none",
        },
      })}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${bankAccountID}-content`}
        id={`panel${bankAccountID}-header`}
      >
        <Box ml={1} mr={2} width="100%">
          <Account
            {...bankAccount}
            balanceVariant="hidden"
            verificationStatusVisibility="all"
            isBankIdentityVerification={isBankIdentityVerification}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={({ spacing }) => ({
          padding: spacing(5),
          borderTop: "1px solid rgba(0, 0, 0, .05)",
        })}
      >
        <Grid container direction="column">
          <Grid container direction="row" spacing={3}>
            <PaperRow
              left={t(
                Resources.Pages.Content.BankAccounts.ConnectedBankAccount.Owner,
              )}
              right={ownersNames.join(", ")}
            />
            {dateCreated && (
              <PaperRow
                left={t(
                  Resources.Pages.Content.BankAccounts.ConnectedBankAccount
                    .DateCreated,
                )}
                right={format(new Date(dateCreated), "dd.MM.yyyy HH:mm")}
              />
            )}
            <PaperRow
              left={t(
                Resources.Pages.Content.BankAccounts.ConnectedBankAccount
                  .Status,
              )}
              rightComponent="div"
              right={
                <Box justifySelf="flex-end" display="flex">
                  <VerificationStatusChip verificationStatus={status} />
                </Box>
              }
            />
            {isBankIdentityVerification && (
              <PaperRow
                left={t(
                  Resources.Pages.Content.BankAccounts.ConnectedBankAccount
                    .BankVerificationSource,
                )}
                rightComponent="div"
                right={
                  <Box justifySelf="flex-end" display="flex">
                    {t(
                      Resources.BankAccounts.BankVerificationSource[
                        bankVerificationSource
                      ],
                    )}
                  </Box>
                }
              />
            )}
          </Grid>
        </Grid>
        {!isReadOnly &&
          (status === BankAccountStatus.AwaitingVerification ||
            status === BankAccountStatus.PreApproved) && (
            <ManualBankAccountVerificationPrompt
              bankAccountPublicID={publicID}
              userID={userID}
            />
          )}
      </AccordionDetails>
    </Accordion>
  );
};
